import { z } from "zod/v4-mini";
import type { ReactNode } from "preact/compat";
import useLocalStorage from "react-use/lib/useLocalStorage";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "preact/compat";
import type {
	AccessToken,
	AuthUser,
} from "@brickme/frontend-common/src/auth/auth-user.ts";
import type {
	SignInConfirmRequest,
	SignInResult,
	FullSignUpUser,
} from "@brickme/frontend-common/src/auth/auth-service.ts";
import { createAuthService } from "@brickme/frontend-common/src/auth/auth-service.ts";
import { createD2cCustomerAuthCookieStorage } from "@brickme/frontend-common/src/auth/d2c-customer-auth-cookie-storage.ts";
import { ensureExhaustive } from "~/utils/never.ts";

// Helper to decode verification token (JWT) without verification
// We only need to extract userId and nonce - the API has already verified the token
function decodeVerificationToken(token: string): {
	userId: string;
	nonce: string;
} | null {
	try {
		const parts = token.split(".");
		if (parts.length !== 3) return null;

		const payload = JSON.parse(atob(parts[1]));
		if (!payload.userId || !payload.nonce) return null;

		return {
			userId: payload.userId,
			nonce: payload.nonce,
		};
	} catch {
		return null;
	}
}

type UpdatableProfile = Partial<
	Pick<
		AuthUser,
		| "firstName"
		| "lastName"
		| "middleName"
		| "birthdate"
		| "country"
		| "howHeard"
		| "emailAddress"
	>
>;

type SignedInAuthenticationResult = {
	readonly type: "signed-in";
	readonly authUser: AuthUser;
	readonly signOut: () => void;
	readonly updateAuthenticatedUser: (user: UpdatableProfile) => Promise<void>;
	readonly ensureValidAccessToken: () => Promise<AccessToken | undefined>;
	readonly resendEmailVerifyCode: () => Promise<void>;
	readonly verifyEmail: (code: string) => Promise<void>;
	readonly forceRefreshTokens: () => Promise<void>;
};

type ConfirmSignUpResult =
	| { readonly type: "success" }
	| { readonly type: "already-confirmed" };

type SignedOutAuthenticationResult = {
	readonly type: "signed-out";
	readonly hasPendingSignUp: boolean;
	readonly signIn: (emailAddress: string) => Promise<SignInResult>;
	readonly confirmSignIn: (request: SignInConfirmRequest) => Promise<void>;
	readonly signUp: (profile: FullSignUpUser) => Promise<void>;
	readonly confirmSignUp: (
		emailAddress: string,
		code: string,
	) => Promise<ConfirmSignUpResult>;
	readonly resendSignUpConfirmationCode: (
		emailAddress: string,
	) => Promise<void>;
	readonly autoLoginAfterEmailVerification: (
		verificationToken: string,
	) => Promise<void>;
};

type LoadingAuthenticationResult = {
	readonly type: "loading";
};

type AuthenticationResult =
	| LoadingAuthenticationResult
	| SignedInAuthenticationResult
	| SignedOutAuthenticationResult;

type AuthenticationState = "anonymous" | "loading" | AuthUser;

const confirmSignUpStateSchema = z.object({
	// Undefined if resend - no longer have a session
	session: z.union([z.string(), z.undefined()]),
});

const AuthStateContext = createContext<AuthenticationResult | undefined>(
	undefined,
);

type AuthStateProviderProps = {
	// For cookie name. So no conflicts if changes.
	readonly cookieDomain: string;
	readonly cookiesSecure: boolean;
	readonly userPoolId: string;
	readonly appClientId: string;
	readonly originSite: string;
	readonly children: ReactNode;
};

function AuthStateProvider({
	userPoolId,
	appClientId,
	originSite,
	cookiesSecure,
	cookieDomain,
	children,
}: AuthStateProviderProps) {
	// Auth service
	const authService = useMemo(
		() =>
			createAuthService({
				userPoolId,
				appClientId,
				originSite,
			}),
		[userPoolId, appClientId, originSite],
	);

	// Cookies
	const cookieStorage = useMemo(
		() =>
			createD2cCustomerAuthCookieStorage({
				appClientId,
				cookieDomain,
				cookiesSecure,
			}),
		[appClientId, cookiesSecure, cookieDomain],
	);

	// Auth state
	const [state, privateSetState] = useState<AuthenticationState>("loading");

	// Load from cookie
	useEffect(() => {
		const storedTokens = cookieStorage.get();
		if (storedTokens !== undefined) {
			(async () => {
				const { refreshToken, accessToken, idToken } = storedTokens;
				const authUser = await authService.validateTokens(
					accessToken,
					idToken,
					refreshToken,
				);

				// Cookie tokens are valid
				if (authUser !== undefined) {
					privateSetState(authUser);
					return;
				}

				// Try and refresh
				const refreshedUser = await authService.refreshAuthUser(refreshToken);
				if (refreshedUser !== undefined) {
					privateSetState(refreshedUser);
					return;
				}

				privateSetState("anonymous");
			})();
			return;
		}

		privateSetState("anonymous");
	}, [cookieStorage, authService]);

	const setState = useCallback(
		(newState: Exclude<AuthenticationState, "loading">) => {
			if (newState === "anonymous") {
				cookieStorage.clear();
				privateSetState(newState);
				return;
			}

			cookieStorage.set(newState);
			privateSetState(newState);
		},
		[],
	);

	const [confirmSignUpState, setConfirmSignUpState, removeConfirmSignUpState] =
		useLocalStorage("confirmSignUp", undefined, {
			raw: false,
			serializer: JSON.stringify,
			deserializer(serialised) {
				try {
					const result = confirmSignUpStateSchema.safeParse(
						JSON.parse(serialised),
					);
					if (!result.success) {
						return undefined;
					}

					return result.data;
				} catch (e) {
					console.error("Couldn't deserialise confirmSignUp", e);
					return undefined;
				}
			},
		});

	const contextValue = useMemo((): AuthenticationResult => {
		if (state === "loading") {
			return { type: "loading" };
		}

		if (state === "anonymous") {
			return {
				type: "signed-out",
				async signUp(profile: FullSignUpUser) {
					const { session } = await authService.fullSignUp(profile);
					setConfirmSignUpState({ session });
				},
				hasPendingSignUp: confirmSignUpState !== undefined,
				async confirmSignUp(emailAddress: string, code: string) {
					if (confirmSignUpState === undefined) {
						throw new Error("Invalid state, no confirmSignUpState");
					}
					const confirmResult = await authService.confirmSignUpAndSignIn({
						emailAddress,
						session: undefined,
						code,
					});
					switch (confirmResult.type) {
						case "success": {
							setState(confirmResult.user);
							removeConfirmSignUpState();
							return { type: "success" } as const;
						}
						case "already-confirmed":
							removeConfirmSignUpState();
							return { type: "already-confirmed" } as const;
						default:
							ensureExhaustive(confirmResult);
					}
				},
				async resendSignUpConfirmationCode(emailAddress: string) {
					await authService.resendSignUpConfirmationCode(emailAddress);
					setConfirmSignUpState({ session: undefined });
				},
				signIn(emailAddress: string) {
					return authService.signIn(emailAddress);
				},
				async confirmSignIn(request: SignInConfirmRequest) {
					const confirmedUser = await authService.signInConfirm(request);
					setState(confirmedUser);
				},
				async autoLoginAfterEmailVerification(verificationToken: string) {
					// Decode the verification token to extract userId and nonce
					const decoded = decodeVerificationToken(verificationToken);
					if (!decoded) {
						throw new Error("Invalid verification token format");
					}

					// Use custom auth flow with email verification
					const user = await authService.signInWithEmailVerification(
						decoded.userId,
						decoded.nonce,
					);
					setState(user);
				},
			};
		}

		async function ensureValidUser() {
			if (state === "anonymous" || state === "loading") {
				throw new Error("Invalid state - not authenticated");
			}
			const newUser = await authService.validateTokens(
				state.accessToken,
				state.idToken,
				state.refreshToken,
			);

			// Current tokens are valid
			if (newUser !== undefined) {
				setState(newUser);
				return newUser;
			}

			// Not valid, try and refresh
			const refreshedUser = await authService.refreshAuthUser(
				state.refreshToken,
			);
			if (refreshedUser !== undefined) {
				setState(refreshedUser);
				return refreshedUser;
			}

			// No valid user and couldn't refresh
			setState("anonymous");
			return undefined;
		}

		return {
			type: "signed-in",
			authUser: state,
			signOut() {
				setState("anonymous");
			},
			async updateAuthenticatedUser(updates: UpdatableProfile): Promise<void> {
				const validUser = await ensureValidUser();
				if (validUser === undefined) {
					throw new Error("Invalid tokens");
				}
				const newUser = await authService.updateAttributes(validUser, updates);
				setState(newUser ?? "anonymous");
			},
			async ensureValidAccessToken() {
				const newUser = await ensureValidUser();
				return newUser?.accessToken;
			},
			async resendEmailVerifyCode() {
				const validUser = await ensureValidUser();
				if (validUser === undefined) {
					throw new Error("Invalid tokens");
				}
				await authService.resendEmailVerifyCode(validUser);
			},
			async verifyEmail(code: string) {
				const validUser = await ensureValidUser();
				if (validUser === undefined) {
					throw new Error("Invalid tokens");
				}
				const newUser = await authService.verifyEmail(validUser, code);
				setState(newUser ?? "anonymous");
			},
			async forceRefreshTokens() {
				const validUser = await ensureValidUser();
				if (validUser === undefined) {
					throw new Error("Invalid tokens");
				}
				const refreshedUser = await authService.refreshAuthUser(
					state.refreshToken,
				);
				setState(refreshedUser ?? "anonymous");
			},
		};
	}, [state]);

	return (
		<AuthStateContext.Provider value={contextValue}>
			{children}
		</AuthStateContext.Provider>
	);
}

function isLoadingAuthenticationResult(
	result: AuthenticationResult,
): result is LoadingAuthenticationResult {
	return result.type === "loading";
}

function isSignedInAuthenticationResult(
	result: AuthenticationResult,
): result is SignedInAuthenticationResult {
	return result.type === "signed-in";
}

function getSignedInInitials(result: SignedInAuthenticationResult) {
	if (result.authUser.firstName && result.authUser.lastName) {
		return (
			result.authUser.firstName.charAt(0) + result.authUser.lastName.charAt(0)
		);
	}
	return result.authUser.emailAddress.slice(0, 2);
}

function isSignedOutAuthenticationResult(
	result: AuthenticationResult,
): result is SignedOutAuthenticationResult {
	return result.type === "signed-out";
}

function useAuthenticationState() {
	const userState = useContext(AuthStateContext);
	if (userState === undefined) {
		throw new Error("Auth context missing");
	}
	return userState;
}

/**
 * Helper mainly for Typescript, but also for catching any issues loudly.
 * Should use in private only pages.
 */
function useSignedInAuthenticationResult(): SignedInAuthenticationResult {
	const result = useAuthenticationState();
	if (!isSignedInAuthenticationResult(result)) {
		throw new Error("User not in signed in state");
	}
	return result;
}

/**
 * Helper mainly for Typescript, but also for catching any issues loudly.
 * Should use in public only pages.
 */
function useSignedOutAuthenticationResult(): SignedOutAuthenticationResult {
	const result = useAuthenticationState();
	if (!isSignedOutAuthenticationResult(result)) {
		throw new Error("User not in signed out state");
	}
	return result;
}

export type {
	SignedInAuthenticationResult,
	AuthenticationResult,
	AuthenticationState,
};
export {
	AuthStateProvider,
	isLoadingAuthenticationResult,
	useAuthenticationState,
	isSignedInAuthenticationResult,
	isSignedOutAuthenticationResult,
	useSignedInAuthenticationResult,
	useSignedOutAuthenticationResult,
	getSignedInInitials,
};
